import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ConfirmationService, MessageService } from 'primeng/api';
import { Subject, takeUntil, timer } from 'rxjs';
import { StorageService } from 'src/app/modules/shared/services/storage.service';
import { BlockUIService } from 'src/app/modules/shared/services/blockUI.service';
import { resolveCompanyId } from 'src/app/utils/tenant-context.util';
import { WorkCenter } from 'src/app/interfaces/base-entity';
import { CorpacerosSyncService } from '../../../epps/services/corpaceros-sync.service';
import { CorpacerosInventoryIntegrationConfigService } from '../../../epps/services/corpaceros-inventory-integration-config.service';
import { CorpacerosWarehouseMappingService } from '../../../epps/services/corpaceros-warehouse-mapping.service';
import { WorkCenterService } from '../../services/workcenter.service';
import {
  CorpacerosSyncCompletedEvent,
  CorpacerosSyncFailedEvent,
  CorpacerosSyncProgressEvent,
  CorpacerosSyncStartedEvent,
  InventorySyncLogDTO,
  InventorySyncLogsRequest,
  InventorySyncLogStatus,
} from '../../../epps/models/corpaceros-sync.models';
import {
  CorpacerosInventoryIntegrationConfigDTO,
  CorpacerosInventoryPlaceholder,
  CorpacerosInventoryWorkCenterDTO,
  SaveCorpacerosInventoryIntegrationConfigCommand,
  TestCorpacerosInventoryConnectionCommand,
} from '../../../epps/models/corpaceros-inventory-integration-config.models';
import {
  CorpacerosWarehouseMappingDTO,
  SaveCorpacerosWarehouseMappingCommand,
} from '../../../epps/models/corpaceros-warehouse-mapping.models';
import { formatInventorySyncJson } from '../../../epps/utils/inventory-sync-json.util';

type InventorySyncStatus = 'inactivo' | 'iniciando' | 'en-progreso' | 'completado' | 'error' | 'timeout';

@Component({
  selector: 'app-inventory-sync-settings',
  templateUrl: './inventory-sync-settings.component.html',
  providers: [ConfirmationService],
})
export class InventorySyncSettingsComponent implements OnInit, OnDestroy {
  private syncService = inject(CorpacerosSyncService);
  private configService = inject(CorpacerosInventoryIntegrationConfigService);
  private mappingService = inject(CorpacerosWarehouseMappingService);
  private workCenterService = inject(WorkCenterService);
  private storage = inject(StorageService);
  private confirmationService = inject(ConfirmationService);
  private messageService = inject(MessageService);
  private blockUI = inject(BlockUIService);
  private fb = inject(FormBuilder);

  companyId: number | null = null;
  status: InventorySyncStatus = 'inactivo';

  // --- Centro de trabajo / bodega Corpaceros (requerido por el endpoint de sincronización) ---
  workCenters: CorpacerosInventoryWorkCenterDTO[] = [];
  workCentersLoading = false;
  workCenterSelected: number | null = null;
  /** Manual sync: true = solo EPPs del día (Ejecucion=0). Se inicializa desde config.soloDelDiaDefault. */
  soloDelDiaManual = true;

  // --- Mapeos bodega ↔ WC ---
  mappings: CorpacerosWarehouseMappingDTO[] = [];
  mappingsLoading = false;
  showMappingForm = false;
  mappingMode: 'add' | 'edit' = 'add';
  mappingForm!: FormGroup;
  mappingId: number | null = null;
  companyWorkCenters: WorkCenter[] = [];
  companyWorkCentersLoading = false;

  // --- Estado historial ---
  logs: InventorySyncLogDTO[] = [];
  logsLoading = false;
  totalLogs = 0;
  logPage = 1;
  logPageSize = 20;
  logFirst = 0;
  filterForm!: FormGroup;

  statusOptions: { label: string; value: InventorySyncLogStatus | null }[] = [
    { label: 'Todos', value: null },
    { label: 'Pendiente', value: 'PENDING' },
    { label: 'En progreso', value: 'IN_PROGRESS' },
    { label: 'Completado', value: 'COMPLETED' },
    { label: 'Fallido', value: 'FAILED' },
  ];

  // --- Estado configuración de conexión ---
  configForm!: FormGroup;
  configId: number | null = null;
  config: CorpacerosInventoryIntegrationConfigDTO | null = null;
  showConfigForm = false;
  configMode: 'add' | 'edit' = 'add';
  hasPasswordConfigured = false;
  configLoading = false;
  testingConnection = false;
  availablePlaceholders: CorpacerosInventoryPlaceholder[] = [];
  placeholdersLoading = false;
  readonly templatePlaceholder = '{\n  "Ejecucion": "{{ejecucion}}",\n  "Bodega": "{{bodega}}"\n}';
  readonly timeZoneOptions = [
    { label: 'America/Bogota (Colombia)', value: 'America/Bogota' },
    { label: 'UTC', value: 'UTC' },
    { label: 'America/Mexico_City', value: 'America/Mexico_City' },
    { label: 'America/Lima', value: 'America/Lima' },
  ];
  readonly defaultCronHint = '0 0 23 * * *';

  syncId: string | null = null;
  totalBlocks = 0;
  currentBlock = 0;
  itemsProcessed = 0;
  totalItems = 0;
  progress = 0;

  totalItemsProcessed = 0;
  eppsCreated = 0;
  eppsUpdated = 0;
  inventoryRecordsCreated = 0;
  batchesCreated = 0;
  errors = 0;
  errorMessages: string[] = [];

  errorMessage = '';

  selectedLog: InventorySyncLogDTO | null = null;
  showLogDetailDialog = false;
  showPayloadDialog = false;
  showResponseDialog = false;

  private readonly TIMEOUT_MS = 60000;
  private timeoutTimer$ = new Subject<void>();
  private destroy$ = new Subject<void>();

  ngOnInit(): void {
    const currentCompany = this.storage.getItem('currentCompany', false) as any;
    this.companyId = resolveCompanyId(currentCompany);

    this.buildConfigForm();
    this.buildMappingForm();
    this.buildFilterForm();
    this.loadWorkCenters();
    this.loadMappings();
    this.loadCompanyWorkCenters();
    this.loadConfig();
    this.loadPlaceholders();
    this.subscribeToSyncEvents();
  }

  private loadWorkCenters(): void {
    if (!this.companyId) return;
    this.workCentersLoading = true;
    this.configService.getWorkCenters(this.companyId).subscribe({
      next: (res) => {
        this.workCenters = res.data ?? [];
        if (this.workCenters.length > 0) {
          this.workCenterSelected = this.workCenters[0].workCenterId;
        }
        this.workCentersLoading = false;
      },
      error: () => {
        this.workCentersLoading = false;
      },
    });
  }

  private loadCompanyWorkCenters(): void {
    this.companyWorkCentersLoading = true;
    this.workCenterService.getAll().subscribe({
      next: (list) => {
        this.companyWorkCenters = Array.isArray(list) ? list : [];
        this.companyWorkCentersLoading = false;
      },
      error: () => {
        this.companyWorkCenters = [];
        this.companyWorkCentersLoading = false;
      },
    });
  }

  private loadMappings(): void {
    if (!this.companyId) return;
    this.mappingsLoading = true;
    this.mappingService.getByCompany(this.companyId).subscribe({
      next: (res) => {
        this.mappings = res.data ?? [];
        this.mappingsLoading = false;
      },
      error: () => {
        this.mappings = [];
        this.mappingsLoading = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'No se pudieron cargar los mapeos de bodega',
        });
      },
    });
  }

  private buildMappingForm(): void {
    this.mappingForm = this.fb.group({
      corpacerosWarehouseCode: ['', [Validators.required, Validators.maxLength(50)]],
      corpacerosWarehouseName: ['', Validators.maxLength(200)],
      workCenterId: [null, Validators.required],
      isActive: [true],
    });
  }

  openNewMapping(): void {
    this.mappingMode = 'add';
    this.mappingId = null;
    this.mappingForm.reset({
      corpacerosWarehouseCode: '',
      corpacerosWarehouseName: '',
      workCenterId: null,
      isActive: true,
    });
    this.showMappingForm = true;
  }

  openEditMapping(row: CorpacerosWarehouseMappingDTO): void {
    this.mappingMode = 'edit';
    this.mappingId = row.id;
    this.mappingForm.patchValue({
      corpacerosWarehouseCode: row.corpacerosWarehouseCode,
      corpacerosWarehouseName: row.corpacerosWarehouseName ?? '',
      workCenterId: row.workCenterId,
      isActive: row.isActive,
    });
    this.showMappingForm = true;
  }

  cancelMappingForm(): void {
    this.showMappingForm = false;
    this.mappingId = null;
  }

  saveMapping(): void {
    if (!this.companyId || this.mappingForm.invalid) {
      this.mappingForm.markAllAsTouched();
      return;
    }

    const raw = this.mappingForm.getRawValue();
    const command: SaveCorpacerosWarehouseMappingCommand = {
      id: this.mappingId,
      companyId: this.companyId,
      corpacerosWarehouseCode: String(raw.corpacerosWarehouseCode).trim(),
      corpacerosWarehouseName: raw.corpacerosWarehouseName
        ? String(raw.corpacerosWarehouseName).trim()
        : null,
      workCenterId: Number(raw.workCenterId),
      isActive: !!raw.isActive,
    };

    this.blockUI.start();
    this.mappingService.save(command).subscribe({
      next: (res) => {
        this.blockUI.stop();
        if (!res.success) {
          return;
        }
        this.messageService.add({
          severity: 'success',
          summary: 'Guardado',
          detail: 'Mapeo de bodega guardado',
        });
        this.showMappingForm = false;
        this.loadMappings();
        this.loadWorkCenters();
      },
      error: () => {
        this.blockUI.stop();
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'No se pudo guardar el mapeo',
        });
      },
    });
  }

  confirmDeleteMapping(event: Event, row: CorpacerosWarehouseMappingDTO): void {
    this.confirmationService.confirm({
      target: event.target as EventTarget,
      message: `¿Eliminar el mapeo de "${row.corpacerosWarehouseCode}"?`,
      icon: 'pi pi-trash',
      acceptLabel: 'Eliminar',
      rejectLabel: 'Cancelar',
      acceptButtonStyleClass: 'p-button-danger p-button-sm',
      accept: () => this.deleteMapping(row.id),
    });
  }

  private deleteMapping(id: number): void {
    this.blockUI.start();
    this.mappingService.delete(id).subscribe({
      next: (res) => {
        this.blockUI.stop();
        if (!res.success) {
          return;
        }
        this.messageService.add({
          severity: 'success',
          summary: 'Eliminado',
          detail: 'Mapeo eliminado',
        });
        this.loadMappings();
        this.loadWorkCenters();
      },
      error: () => {
        this.blockUI.stop();
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'No se pudo eliminar el mapeo',
        });
      },
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.timeoutTimer$.next();
    this.timeoutTimer$.complete();
  }

  confirmStart(event: Event): void {
    if (!this.workCenterSelected) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Seleccione un centro de trabajo antes de sincronizar',
      });
      return;
    }
    this.confirmationService.confirm({
      target: event.target as EventTarget,
      message: '¿Desea iniciar la sincronización de inventario con Corpaceros?',
      icon: 'pi pi-sync',
      acceptLabel: 'Sí, sincronizar',
      rejectLabel: 'Cancelar',
      acceptButtonStyleClass: 'p-button-info p-button-sm',
      accept: () => this.startSync(),
    });
  }

  startSync(): void {
    if (!this.companyId || !this.workCenterSelected) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'No se ha seleccionado una compañía o centro de trabajo válido',
      });
      return;
    }

    this.resetState();
    this.status = 'iniciando';
    this.startTimeoutTimer();

    // Sync manual siempre es histórico completo; "solo del día" lo maneja la Azure Function diaria.
    this.syncService.startSync(this.companyId, this.workCenterSelected, this.soloDelDiaManual).subscribe({
      error: () => {
        this.status = 'error';
        this.errorMessage = 'No se pudo iniciar la sincronización. Intente nuevamente.';
      },
    });
  }

  private subscribeToSyncEvents(): void {
    this.syncService.syncStarted$
      .pipe(takeUntil(this.destroy$))
      .subscribe((event: CorpacerosSyncStartedEvent) => {
        this.timeoutTimer$.next();
        this.status = 'en-progreso';
        this.syncId = event.syncId;
        this.totalItems = event.totalItems;
        this.totalBlocks = event.totalBlocks;
      });

    this.syncService.syncProgress$
      .pipe(takeUntil(this.destroy$))
      .subscribe((event: CorpacerosSyncProgressEvent) => {
        this.currentBlock = event.blockNumber;
        this.totalBlocks = event.totalBlocks;
        this.itemsProcessed = event.itemsProcessed;
        this.totalItems = event.totalItems;
        this.eppsCreated = event.eppsCreated;
        this.eppsUpdated = event.eppsUpdated;
        this.errors = event.errors;
        this.progress = this.totalItems > 0 ? Math.round((this.itemsProcessed / this.totalItems) * 100) : 0;
      });

    this.syncService.syncCompleted$
      .pipe(takeUntil(this.destroy$))
      .subscribe((event: CorpacerosSyncCompletedEvent) => {
        this.status = 'completado';
        this.progress = 100;
        this.totalItemsProcessed = event.totalItemsProcessed;
        this.eppsCreated = event.eppsCreated;
        this.eppsUpdated = event.eppsUpdated;
        this.inventoryRecordsCreated = event.inventoryRecordsCreated;
        this.batchesCreated = event.batchesCreated;
        this.errors = event.errors;
        this.errorMessages = event.errorMessages || [];
      });

    this.syncService.syncFailed$
      .pipe(takeUntil(this.destroy$))
      .subscribe((event: CorpacerosSyncFailedEvent) => {
        this.status = 'error';
        this.errorMessage = event.error;
      });
  }

  private startTimeoutTimer(): void {
    timer(this.TIMEOUT_MS)
      .pipe(takeUntil(this.timeoutTimer$), takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.status === 'iniciando') {
          this.status = 'timeout';
          this.errorMessage = 'Tiempo de espera agotado. No se recibió respuesta de Corpaceros.';
        }
      });
  }

  private resetState(): void {
    this.syncId = null;
    this.totalBlocks = 0;
    this.currentBlock = 0;
    this.itemsProcessed = 0;
    this.totalItems = 0;
    this.progress = 0;
    this.totalItemsProcessed = 0;
    this.eppsCreated = 0;
    this.eppsUpdated = 0;
    this.inventoryRecordsCreated = 0;
    this.batchesCreated = 0;
    this.errors = 0;
    this.errorMessages = [];
    this.errorMessage = '';
  }

  // ============================================================
  // CONFIGURACIÓN DE CONEXIÓN (JDE / Corpaceros)
  // ============================================================
  private buildConfigForm(): void {
    this.configForm = this.fb.group({
      name: ['', Validators.required],
      description: [''],
      isActive: [true],
      isAutoSync: [true],
      soloDelDiaDefault: [true],
      cronExpression: ['0 0 23 * * *'],
      timeZoneId: ['America/Bogota'],
      inventoryApiUrl: ['', [Validators.required, Validators.pattern('https?://.+')]],
      username: ['', Validators.required],
      password: [''],
      timeoutSeconds: [30, [Validators.required, Validators.min(1)]],
      maxRetries: [3, [Validators.required, Validators.min(0)]],
      payloadTemplate: [''],
    });
  }

  loadPlaceholders(): void {
    this.placeholdersLoading = true;
    this.configService.getAvailablePlaceholders().subscribe({
      next: (data) => {
        this.availablePlaceholders = data ?? [];
        this.placeholdersLoading = false;
      },
      error: () => {
        this.placeholdersLoading = false;
      },
    });
  }

  copyPlaceholder(key: string): void {
    navigator.clipboard.writeText(key).then(() => {
      this.messageService.add({
        severity: 'info',
        summary: 'Copiado',
        detail: `${key} copiado al portapapeles`,
        life: 2000,
      });
    });
  }

  private loadConfig(): void {
    if (!this.companyId) return;
    this.configLoading = true;
    this.configService.getByCompany(this.companyId).subscribe({
      next: (res) => {
        this.config = res.data ?? null;
        this.configId = this.config?.id ?? null;
        this.hasPasswordConfigured = this.config?.hasPasswordConfigured ?? false;
        this.soloDelDiaManual = this.config?.soloDelDiaDefault ?? true;
        this.updatePasswordValidator();
        this.configLoading = false;
      },
      error: () => {
        this.configLoading = false;
      },
    });
  }

  private updatePasswordValidator(): void {
    const passwordControl = this.configForm.get('password');
    if (!this.configId) {
      passwordControl?.setValidators([Validators.required]);
    } else {
      passwordControl?.clearValidators();
    }
    passwordControl?.updateValueAndValidity();
  }

  private resetConfigForm(): void {
    this.configForm.reset({
      name: '',
      description: '',
      isActive: true,
      isAutoSync: true,
      soloDelDiaDefault: true,
      cronExpression: '0 0 23 * * *',
      timeZoneId: 'America/Bogota',
      inventoryApiUrl: '',
      username: '',
      password: '',
      timeoutSeconds: 30,
      maxRetries: 3,
      payloadTemplate: '',
    });
    this.updatePasswordValidator();
  }

  openNewConfig(): void {
    this.configMode = 'add';
    this.configId = null;
    this.hasPasswordConfigured = false;
    this.resetConfigForm();
    this.showConfigForm = true;
  }

  openEditConfig(): void {
    if (!this.config) return;
    this.configMode = 'edit';
    this.configId = this.config.id;
    this.hasPasswordConfigured = this.config.hasPasswordConfigured;
    this.configForm.patchValue({
      name: this.config.name,
      description: this.config.description,
      isActive: this.config.isActive,
      isAutoSync: this.config.isAutoSync,
      soloDelDiaDefault: this.config.soloDelDiaDefault ?? true,
      cronExpression: this.config.cronExpression ?? '0 0 23 * * *',
      timeZoneId: this.config.timeZoneId ?? 'America/Bogota',
      inventoryApiUrl: this.config.inventoryApiUrl,
      username: this.config.username,
      timeoutSeconds: this.config.timeoutSeconds,
      maxRetries: this.config.maxRetries,
      payloadTemplate: this.config.payloadTemplate ?? '',
    });
    this.configForm.get('password')?.reset('');
    this.updatePasswordValidator();
    this.showConfigForm = true;
  }

  closeConfigForm(): void {
    this.showConfigForm = false;
    this.resetConfigForm();
  }

  confirmDeleteConfig(event: Event): void {
    if (!this.configId) return;
    const idToDelete = this.configId;
    this.confirmationService.confirm({
      target: event.target as EventTarget,
      message: '¿Desea eliminar la configuración de conexión?',
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-danger p-button-sm',
      accept: () => {
        this.blockUI.start();
        this.configService.delete(idToDelete).subscribe({
          next: () => {
            this.config = null;
            this.configId = null;
            this.hasPasswordConfigured = false;
            this.messageService.add({
              severity: 'success',
              summary: 'Eliminado',
              detail: 'Configuración eliminada correctamente',
            });
          },
          error: () => {
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'No se pudo eliminar la configuración',
            });
            this.blockUI.stop();
          },
          complete: () => this.blockUI.stop(),
        });
      },
    });
  }

  saveConfig(): void {
    if (!this.companyId) return;
    if (this.configForm.invalid) {
      this.configForm.markAllAsTouched();
      return;
    }

    const raw = this.configForm.getRawValue();
    const command: SaveCorpacerosInventoryIntegrationConfigCommand = {
      id: this.configId,
      companyId: this.companyId,
      name: raw.name,
      description: raw.description || null,
      isActive: raw.isActive,
      isAutoSync: raw.isAutoSync,
      soloDelDiaDefault: raw.soloDelDiaDefault,
      cronExpression: raw.cronExpression?.trim() || null,
      timeZoneId: raw.timeZoneId || 'America/Bogota',
      inventoryApiUrl: raw.inventoryApiUrl,
      username: raw.username,
      password: raw.password ? raw.password : null,
      timeoutSeconds: raw.timeoutSeconds,
      maxRetries: raw.maxRetries,
      payloadTemplate: raw.payloadTemplate ?? null,
    };

    this.blockUI.start();
    this.configService.save(command).subscribe({
      next: (res) => {
        this.configId = res.data ?? this.configId;
        this.hasPasswordConfigured = this.hasPasswordConfigured || !!raw.password;
        this.soloDelDiaManual = raw.soloDelDiaDefault;
        this.config = {
          id: this.configId!,
          companyId: this.companyId!,
          name: raw.name,
          description: raw.description || null,
          isActive: raw.isActive,
          isAutoSync: raw.isAutoSync,
          soloDelDiaDefault: raw.soloDelDiaDefault,
          cronExpression: raw.cronExpression?.trim() || null,
          timeZoneId: raw.timeZoneId || 'America/Bogota',
          inventoryApiUrl: raw.inventoryApiUrl,
          username: raw.username,
          timeoutSeconds: raw.timeoutSeconds,
          maxRetries: raw.maxRetries,
          hasPasswordConfigured: this.hasPasswordConfigured,
          payloadTemplate: raw.payloadTemplate ?? null,
        };
        this.messageService.add({
          severity: 'success',
          summary: 'Guardado',
          detail: this.configMode === 'add' ? 'Configuración creada correctamente' : 'Configuración actualizada correctamente',
        });
        this.showConfigForm = false;
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'No se pudo guardar la configuración de conexión',
        });
        this.blockUI.stop();
      },
      complete: () => this.blockUI.stop(),
    });
  }

  // ============================================================
  // HISTORIAL
  // ============================================================
  private buildFilterForm(): void {
    this.filterForm = this.fb.group({
      status: [null],
      workCenterId: [null],
      dateFrom: [null],
      dateTo: [null],
    });
  }

  loadLogs(page = 1): void {
    if (!this.companyId) return;
    const f = this.filterForm.getRawValue();
    this.logPage = page;
    this.logsLoading = true;

    const req: InventorySyncLogsRequest = {
      companyId: this.companyId,
      workCenterId: f.workCenterId ?? null,
      pageNumber: page,
      pageSize: this.logPageSize,
      status: f.status ?? null,
      dateFrom: f.dateFrom ? (f.dateFrom as Date).toISOString() : null,
      dateTo: f.dateTo ? (f.dateTo as Date).toISOString() : null,
    };

    this.syncService.getLogs(req).subscribe({
      next: (res) => {
        const data = res.data;
        this.logs = data?.items ?? [];
        this.totalLogs = data?.totalCount ?? 0;
        this.logsLoading = false;
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'No se pudo cargar el historial',
        });
        this.logsLoading = false;
      },
    });
  }

  onLogPageChange(event: any): void {
    this.logPageSize = event.rows;
    this.logFirst = event.first;
    this.loadLogs(Math.floor(event.first / event.rows) + 1);
  }

  clearLogFilters(): void {
    this.filterForm.reset({ status: null, workCenterId: null, dateFrom: null, dateTo: null });
    this.loadLogs(1);
  }

  workCenterName(workCenterId: number): string {
    return this.workCenters.find((w) => w.workCenterId === workCenterId)?.workCenterName ?? `#${workCenterId}`;
  }

  getStatusSeverity(status: InventorySyncLogStatus): 'success' | 'secondary' | 'info' | 'warning' | 'danger' {
    const map: Record<InventorySyncLogStatus, 'success' | 'secondary' | 'info' | 'warning' | 'danger'> = {
      PENDING: 'warning',
      IN_PROGRESS: 'info',
      COMPLETED: 'success',
      FAILED: 'danger',
    };
    return map[status] ?? 'secondary';
  }

  getStatusLabel(status: InventorySyncLogStatus): string {
    const map: Record<InventorySyncLogStatus, string> = {
      PENDING: 'Pendiente',
      IN_PROGRESS: 'En progreso',
      COMPLETED: 'Completado',
      FAILED: 'Fallido',
    };
    return map[status] ?? status;
  }

  openLogDetail(log: InventorySyncLogDTO): void {
    this.selectedLog = log;
    this.showLogDetailDialog = true;
  }

  viewPayload(log: InventorySyncLogDTO): void {
    this.selectedLog = log;
    if (!log.payloadSent) {
      this.messageService.add({
        severity: 'info',
        summary: 'Sin payload',
        detail: 'Este registro no tiene payload guardado. Solo las sincronizaciones nuevas lo incluyen.',
      });
    }
    this.showPayloadDialog = true;
  }

  viewResponse(log: InventorySyncLogDTO): void {
    this.selectedLog = log;
    if (!log.responseReceived) {
      this.messageService.add({
        severity: 'info',
        summary: 'Sin respuesta',
        detail: 'Este registro no tiene respuesta/resumen guardado. Solo las sincronizaciones nuevas lo incluyen.',
      });
    }
    this.showResponseDialog = true;
  }

  prettyJson(raw: string | null | undefined): string {
    return formatInventorySyncJson(raw);
  }

  parseErrorMessages(log: InventorySyncLogDTO | null): string[] {
    if (!log?.errorMessages?.length) {
      return [];
    }
    return log.errorMessages;
  }

  testConnection(): void {
    const raw = this.configForm.getRawValue();

    if (!raw.inventoryApiUrl || !raw.username) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Datos incompletos',
        detail: 'Completa la URL y el usuario antes de probar la conexión',
      });
      return;
    }

    if (!this.configId && !raw.password) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Datos incompletos',
        detail: 'Ingresa la contraseña antes de probar la conexión',
      });
      return;
    }

    const command: TestCorpacerosInventoryConnectionCommand = {
      companyId: this.companyId!,
      id: this.configId,
      inventoryApiUrl: raw.inventoryApiUrl,
      username: raw.username,
      password: raw.password ? raw.password : null,
      timeoutSeconds: raw.timeoutSeconds,
    };

    this.testingConnection = true;
    this.configService.test(command).subscribe({
      next: (res) => {
        const result = res.data;
        this.messageService.add({
          severity: result?.success ? 'success' : 'error',
          summary: result?.success ? 'Conexión exitosa' : 'Conexión fallida',
          detail: result?.message ?? '',
        });
        this.testingConnection = false;
      },
      error: (err) => {
        const status = err?.status ?? 0;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: status
            ? `No se pudo probar la conexión (HTTP ${status})`
            : 'No se pudo probar la conexión',
        });
        this.testingConnection = false;
      },
    });
  }
}
